// dashboard/catalog.js — the widget types this build offers.
//
// It lives under pages/ rather than lib/ because most entries name a PAGE component. `lib/` never
// imports a page (src/CLAUDE.md), and the registry mechanism in lib/widgets/registry.js holds to
// that: it knows how to hold a type, not which types exist.
//
// Importing this module is what registers them, so it is imported once by the dashboard. Every
// `load` is a dynamic import, so none of these components is in the dashboard's own chunk.

import { registerWidget } from "../../lib/widgets/registry.js";

// A `type` string is PERSISTED in every layout that uses it. Renaming one orphans those widgets
// into "no longer available" tiles on other people's dashboards. Treat a shipped type as immutable.

// The twelve figures as one block. Superseded by the individual tiles below and no longer offered in
// the catalog, but still REGISTERED: a shipped type is immutable, and a layout stored before the
// split holds one. Such a layout is expanded into the twelve on load (dashboardStore), so this
// renders only for anything that slipped past that.
registerWidget({
  type: "fleet.summary",
  label: "Fleet summary",
  icon: "layout-dashboard",
  group: "Fleet",
  hidden: true,
  describe: () => "Fleet summary",
  // `minPx` rather than a column count: the constraint is a WIDTH. Below about 380px the console's
  // toolbar, the services grid and a game card's title all overflow, and how many columns 380px
  // buys depends on the breakpoint — six columns is half a wide grid and the whole of a narrow one.
  // See layout.js `columnsForPx`.
  size: { w: 12, h: 2, minPx: 380, minH: 2 },
  load: () => import("./widgets/FleetSummary.jsx").then(m => m.FleetSummary),
});

// ---- The twelve glance tiles ----------------------------------------------
// Each placeable alone, so a dashboard can carry exactly the figures somebody cares about. At w:2
// they read 6, 4, 3 and 2 across the breakpoint ladder — the band's original shape, now as twelve
// things that can be moved and removed independently.
//
// The split is by question, not by source: the first six ask "what is happening on the fleet", the
// second six "what needs me".
const TILE = (type, label, icon, group, comp) => registerWidget({
  type, label, icon, group,
  describe: () => label,
  size: { w: 2, h: 1, minW: 2, minH: 1 },
  load: () => import("./widgets/tiles.jsx").then(m => m[comp]),
});

TILE("tile.running", "Running", "server", "Fleet", "TileRunning");
TILE("tile.playersNow", "Players now", "users", "Fleet", "TilePlayersNow");
TILE("tile.playersWeek", "Players \u00b7 7d", "user-round-check", "Fleet", "TilePlayersWeek");
TILE("tile.played", "Played \u00b7 24h", "hourglass", "Fleet", "TilePlayed");
TILE("tile.uptime", "Uptime", "shield-check", "Fleet", "TileUptime");
TILE("tile.timeToReady", "Time to ready", "timer", "Fleet", "TileTimeToReady");

TILE("tile.drift", "Drift", "git-compare-arrows", "Needs me", "TileDrift");
TILE("tile.crashes", "Crashes \u00b7 24h", "server-crash", "Needs me", "TileCrashes");
TILE("tile.updates", "Updates", "circle-arrow-up", "Needs me", "TileUpdates");
TILE("tile.oldestBackup", "Oldest backup", "database-backup", "Needs me", "TileOldestBackup");
TILE("tile.scheduleFails", "Schedule fails", "calendar-x", "Needs me", "TileScheduleFails");
TILE("tile.services", "Services", "boxes", "Needs me", "TileServices");

// The Cluster page's two member cards. A cluster's members are nodes and anchors and the cards
// are separate because the members are, so each is pinned on its own — a dashboard can carry the
// capacity of the machines, the cluster's anchors, or both.
//
// Gated on the roster read (`nav.cluster`), and cluster-wide rather than per-member: each card
// renders EVERY member of its kind at once, so the aggregate check is the right one — somebody who
// holds a read on one node has no business seeing the rest.
//
// A row span is a MINIMUM (`grid-auto-rows: minmax(--widget-row, auto)`), so the smallest span is
// what lets a card state its own height: one member or four, the cell is what the rows measure and
// never a floor they have to fill.
registerWidget({
  type: "cluster.nodes",
  label: "Nodes",
  icon: "server-cog",
  group: "Nodes",
  cap: "nav.cluster",
  describe: () => "Nodes",
  size: { w: 12, h: 1, minPx: 300, minH: 1 },
  load: () => import("../diagnostics/ClusterNodeList.jsx").then(m => m.ClusterNodeList),
});

// Pinned from the Cluster page and not offered here. The card renders only where the cluster has
// an anchor, so offering it from a list would put a permanently empty cell on the dashboard of the
// single-machine install that has none — and there would be nowhere to press to get it back.
registerWidget({
  type: "cluster.anchors",
  label: "Anchors",
  icon: "anchor",
  group: "Nodes",
  cap: "nav.cluster",
  hidden: true,
  describe: () => "Anchors",
  size: { w: 12, h: 1, minPx: 300, minH: 1 },
  load: () => import("../diagnostics/ClusterAnchorList.jsx").then(m => m.ClusterAnchorList),
});

registerWidget({
  type: "alerts.latest",
  label: "Alerts",
  icon: "bell-ring",
  group: "Activity",
  cap: "nav.alerts",
  describe: () => "Alerts",
  size: { w: 6, h: 4, minPx: 380, minH: 3 },
  load: () => import("./widgets/FeedWidgets.jsx").then(m => m.AlertsLatest),
});

registerWidget({
  type: "activity.recent",
  label: "Recent activity",
  icon: "history",
  group: "Activity",
  cap: "nav.audit",
  describe: () => "Recent activity",
  size: { w: 6, h: 4, minPx: 380, minH: 3 },
  load: () => import("./widgets/FeedWidgets.jsx").then(m => m.ActivityRecent),
});

// Runs — cluster-wide, so it takes no `hostId` and is not a bound widget: a run is the thing a
// person started, and the nodes' shares of it are inside. `host.jobs.queued`/`host.jobs.running`
// answer the other question ("what is THIS node doing") and are bound for that reason.
//
// No `cap`: each node answers the read behind it for whoever may see its runs, the same as the
// servers and jobs they can already see. Cancelling is its own request, which the board asks per
// node.
registerWidget({
  type: "fleet.runs",
  label: "Runs",
  icon: "layers",
  group: "Activity",
  describe: () => "Runs",
  // Measured in a browser (visual-harness/runs-board.mjs): a run card holds its head, its meter and
  // its chips on one row down to 300px. At 260 the chips wrap to a second row and everything still
  // reads; at 220 the scope line ("6 servers on 3 nodes") starts being clipped, which is the one
  // thing on the card that cannot be guessed from the rest of it. 300 is the floor because it is the
  // last width that needs no wrapping to stay whole.
  size: { w: 6, h: 4, minPx: 300, minH: 3 },
  load: () => import("../../components/batch/RunsBoard.jsx").then(m => m.RunsBoard),
});

registerWidget({
  type: "servers.rail",
  label: "Servers",
  icon: "server",
  group: "Servers",
  describe: () => "Servers",
  size: { w: 12, h: 4, minPx: 380, minH: 3 },
  load: () => import("./widgets/ServersRail.jsx").then(m => m.ServersRail),
});

registerWidget({
  type: "library.catalog",
  label: "Game catalog",
  icon: "library",
  group: "Servers",
  cap: "nav.library",
  describe: () => "Game catalog",
  size: { w: 12, h: 4, minPx: 380, minH: 3 },
  load: () => import("./widgets/CatalogRail.jsx").then(m => m.CatalogRail),
});

// ---- Layout ----------------------------------------------------------------

// Deliberate empty room. The grid is a flow, so a gap exists only as a consequence of a widget not
// fitting its row — this is the one way to say "leave this space alone" and have the flow respect it.
//
// `repeatable`, because a dashboard may want several and every other type is a single: pinning the
// catalog twice would be two copies of one card, but two spacers are two different spaces. The store
// gives each its own `slot` param so they are distinct targets rather than one widget added twice.
registerWidget({
  type: "layout.spacer",
  label: "Empty space",
  icon: "square-dashed",
  group: "Layout",
  repeatable: true,
  describe: () => "Empty space",
  // No floor at all: emptiness reads fine at any size, and a spacer that refused to be narrow
  // could not do the one job it has.
  size: { w: 2, h: 1, minH: 1 },
  load: () => import("./widgets/Spacer.jsx").then(m => m.Spacer),
});

// ---- Bound widgets ---------------------------------------------------------
// These carry PARAMETERS, so they are pinned from the page that supplies them and never offered by
// the Add-widget catalog — nothing in a list can know which leaf's journal you meant.

registerWidget({
  type: "leaf.logs",
  label: "Leaf journal",
  icon: "scroll-text",
  group: "Nodes",
  // Per NODE, not fleet-wide: somebody granted a node's journal on one node must not see another
  // node's because they hold it somewhere. persona.js calls this "aggregate for reach, scoped for
  // action".
  cap: "host.manage",
  scope: "host",
  params: ["hostId", "leafId"],
  describe: (p) => (p.leafId || "leaf") + " · journal",
  size: { w: 12, h: 5, minPx: 380, minH: 3 },
  load: () => import("../leaf/LeafLogs.jsx").then(m => m.LeafLogs),
});

registerWidget({
  type: "server.console",
  label: "Server console",
  icon: "terminal-square",
  group: "Servers",
  cap: "server.operate",
  scope: "server",
  params: ["serverId"],
  describe: (p) => (p.serverId || "server") + " · console",
  size: { w: 6, h: 5, minPx: 380, minH: 3 },
  load: () => import("./widgets/ServerWidgets.jsx").then(m => m.ServerConsoleWidget),
});

registerWidget({
  type: "host.sensor",
  label: "Sensor",
  icon: "thermometer",
  group: "Nodes",
  scope: "host",
  params: ["hostId", "sensorId"],
  // Two channels differ only by their params, so the title has to be built from them or a dashboard
  // holding the CPU and an SSD shows the same name twice.
  describe: (p) => (p.sensorId || "sensor").split("/").pop() + " \u00b7 " + (p.hostId || "node"),
  // Repeatable: pinning one channel and not another is the entire point of a per-sensor card.
  repeatable: true,
  size: { w: 3, h: 2, minW: 2, minH: 2 },
  // No wrapper: WidgetHost spreads the descriptor's params as props, and SensorTile already takes
  // hostId and sensorId — so the card the page renders is literally the card the dashboard mounts.
  load: () => import("../diagnostics/SensorTile.jsx").then(m => m.SensorTile),
});

registerWidget({
  type: "host.thermal",
  label: "Thermal",
  icon: "thermometer",
  group: "Nodes",
  scope: "host",
  params: ["hostId"],
  describe: (p) => (p.hostId || "node") + " \u00b7 thermal",
  size: { w: 6, h: 4, minPx: 420, minH: 3 },
  load: () => import("../diagnostics/ThermalPanel.jsx").then(m => m.ThermalPanelWidget),
});

registerWidget({
  type: "host.logs",
  label: "Node journal",
  icon: "scroll-text",
  group: "Nodes",
  cap: "host.manage",
  scope: "host",
  params: ["hostId"],
  describe: (p) => (p.hostId || "node") + " \u00b7 journal",
  size: { w: 12, h: 5, minPx: 380, minH: 3 },
  load: () => import("./widgets/NodeWidgets.jsx").then(m => m.HostLogsWidget),
});

registerWidget({
  type: "host.jobs.queued",
  label: "Node queue",
  icon: "hourglass",
  group: "Nodes",
  cap: "host.manage",
  scope: "host",
  params: ["hostId"],
  describe: (p) => (p.hostId || "node") + " · queued",
  // One card, so the floor is where a ROW stops reading rather than where a set of lanes stops
  // fitting. Measured in a browser against a 30-character instance name — every server on the host
  // that shipped this is named short, and a floor measured against those would hold only there: the
  // card reads clean down to 310 and the name starts ellipsizing at 300.
  size: { w: 6, h: 5, minPx: 310, minH: 3 },
  load: () => import("./widgets/NodeWidgets.jsx").then(m => m.HostQueuedJobsWidget),
});

registerWidget({
  type: "host.jobs.running",
  label: "Node running",
  icon: "loader",
  group: "Nodes",
  cap: "host.manage",
  scope: "host",
  params: ["hostId"],
  describe: (p) => (p.hostId || "node") + " · running",
  // Ten pixels narrower than the queue, measured the same way and against the same name: a running
  // row ends in a spinner where a queued one ends in "3rd of 8", and that chip is the whole of the
  // difference. Clean down to 300, ellipsizing at 290.
  size: { w: 6, h: 5, minPx: 300, minH: 3 },
  load: () => import("./widgets/NodeWidgets.jsx").then(m => m.HostRunningJobsWidget),
});

registerWidget({
  type: "host.services",
  label: "Node services",
  icon: "boxes",
  group: "Nodes",
  cap: "host.manage",
  scope: "host",
  params: ["hostId"],
  describe: (p) => (p.hostId || "node") + " \u00b7 services",
  size: { w: 12, h: 5, minPx: 380, minH: 3 },
  load: () => import("./widgets/NodeWidgets.jsx").then(m => m.HostServicesWidget),
});

const SERVER_WIDGET = (type, label, icon, comp, size) => registerWidget({
  type, label, icon,
  group: "Servers",
  // Per SERVER: somebody who may operate one server must not reach another because they hold the
  // verb somewhere. WidgetHost looks the server's host up to decide.
  cap: "server.operate",
  scope: "server",
  params: ["serverId"],
  describe: (p) => (p.serverId || "server") + " \u00b7 " + label.toLowerCase(),
  size,
  load: () => import("./widgets/ServerWidgets.jsx").then(m => m[comp]),
});

SERVER_WIDGET("server.card", "Server", "server", "ServerCardWidget", { w: 3, h: 4, minW: 2, minH: 3 });
SERVER_WIDGET("server.players", "Players", "users", "ServerPlayersWidget", { w: 6, h: 4, minPx: 380, minH: 3 });
SERVER_WIDGET("server.performance", "Performance", "activity", "ServerPerformanceWidget", { w: 12, h: 5, minPx: 380, minH: 4 });

// ---- The dns anchor's cards ------------------------------------------------
// Every one of these is keyed by the CAPABILITY (empty params, never repeatable), not by which
// member currently holds it — a pin follows a failover instead of pointing at a member that has
// stopped answering for the capability. Gated on `nav.cluster`, the same as the Cluster page's own
// member cards: reaching the anchor's page at all already needs it.
const DNS_TILE = (type, label, icon, comp) => registerWidget({
  type, label, icon, group: "Nodes", cap: "nav.cluster",
  describe: () => label,
  size: { w: 2, h: 1, minW: 2, minH: 1 },
  load: () => import("../accounts/dns/dnsKpis.jsx").then(m => m[comp]),
});

DNS_TILE("dns.kpi.names", "Names published", "globe", "DnsKpiNames");
DNS_TILE("dns.kpi.zone", "Zone", "refresh-cw", "DnsKpiZone");
DNS_TILE("dns.kpi.certWeek", "Certificates this week", "badge-check", "DnsKpiCertWeek");
DNS_TILE("dns.kpi.collisions", "Port collisions", "shuffle", "DnsKpiCollisions");
DNS_TILE("dns.kpi.certExpiry", "Next to expire", "timer", "DnsKpiCertExpiry");
DNS_TILE("dns.kpi.certFailed", "Failed orders", "circle-x", "DnsKpiCertFailed");
DNS_TILE("dns.kpi.certInFlight", "In flight", "timer", "DnsKpiCertInFlight");

registerWidget({
  type: "dns.attention",
  label: "DNS · needs a look",
  icon: "triangle-alert",
  group: "Nodes",
  cap: "nav.cluster",
  describe: () => "DNS · needs a look",
  size: { w: 6, h: 4, minPx: 380, minH: 3 },
  load: () => import("../accounts/dns/DnsAttention.jsx").then(m => m.DnsAttention),
});

registerWidget({
  type: "dns.publishing",
  label: "Publishing",
  icon: "globe",
  group: "Nodes",
  cap: "nav.cluster",
  describe: () => "DNS · publishing",
  size: { w: 6, h: 3, minPx: 340, minH: 2 },
  load: () => import("../accounts/dns/DnsPublishing.jsx").then(m => m.DnsPublishing),
});

registerWidget({
  type: "dns.nodes",
  label: "DNS · nodes",
  icon: "server-cog",
  group: "Nodes",
  cap: "nav.cluster",
  describe: () => "DNS · node names",
  size: { w: 12, h: 2, minPx: 460, minH: 2 },
  load: () => import("../accounts/dns/DnsNames.jsx").then(m => m.DnsNodesTable),
});

registerWidget({
  type: "dns.anchors",
  label: "DNS · anchors",
  icon: "anchor",
  group: "Nodes",
  cap: "nav.cluster",
  describe: () => "DNS · anchor names",
  size: { w: 12, h: 2, minPx: 460, minH: 2 },
  load: () => import("../accounts/dns/DnsNames.jsx").then(m => m.DnsAnchorsTable),
});

registerWidget({
  type: "dns.servers",
  label: "DNS · servers",
  icon: "server",
  group: "Nodes",
  cap: "nav.cluster",
  describe: () => "DNS · server names",
  size: { w: 12, h: 3, minPx: 480, minH: 2 },
  load: () => import("../accounts/dns/DnsNames.jsx").then(m => m.DnsServersTable),
});

registerWidget({
  type: "dns.addresses",
  label: "DNS · public addresses",
  icon: "wifi",
  group: "Nodes",
  cap: "nav.cluster",
  describe: () => "DNS · public addresses",
  size: { w: 12, h: 2, minPx: 420, minH: 2 },
  load: () => import("../accounts/dns/DnsNames.jsx").then(m => m.DnsAddressesTable),
});

registerWidget({
  type: "dns.certificates",
  label: "DNS · certificates",
  icon: "badge-check",
  group: "Nodes",
  cap: "nav.cluster",
  describe: () => "DNS · certificates",
  size: { w: 12, h: 4, minPx: 480, minH: 3 },
  load: () => import("../accounts/dns/DnsCertificates.jsx").then(m => m.DnsCertificatesTable),
});
